import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import IdeationManager from 'managers/ideation';
import { IWCCStorageManager } from 'managers/iStorage';
import SimpleTopicManager from 'managers/simpleTopic';
import { TopicManager } from 'managers/topic';
import { SimpleTopic } from 'models/simpleTopic';
import { Topic } from 'models/topic';
import { TopicThread } from 'models/topicThread';
import { DateHelpers } from '../../../helpers/date';
import DiscussionManager from '../../../managers/discussion';
import Discussion from '../../../models/discussion';
import './editButton.html';

const { discussionId, threadEditInterval, now } = settings;

export interface CommentEditButtonVMConfig {
    comment: SubscribableOrNullableValue<TopicThread>
}

@injectable()
export default class CommentEditButtonVM {
    private discussion: Subscribable<Discussion | undefined>
    private simpleTopic: Subscribable<SimpleTopic | undefined>
    private topic: Subscribable<Topic | undefined>
    private comment: Subscribable<TopicThread | undefined>
    private canEdit: Subscribable<boolean>
    private isModeratorUser: Subscribable<boolean>
    private isRankingStage: Subscribable<boolean>
    private isVotingStage: Subscribable<boolean>

    labels = labels;

    constructor(
        @inject(wccModules.componentConfig) config: CommentEditButtonVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager
    ) {
        this.comment = ko.flattenComputed(config.comment);
        this.canEdit = ko.flattenComputed(() => this.checkIfCanBeEdited());

        const topicId = this.comment.pluck(c => c.topicId);

        const currentUserManager = storage.get(CurrentUserManager, { discussionId });
        const user = currentUserManager.pluck('person');
        this.isModeratorUser = user.pluck(u => u.isModerator, false);

        const discussionManager = storage.get(DiscussionManager, { discussionId });
        this.discussion = discussionManager.pluck(m => m.discussion);

        const simpleTopicManager = storage.get(SimpleTopicManager, { topicId });
        this.simpleTopic = simpleTopicManager.pluck(m => m.topic);

        const topicManager = storage.get(TopicManager, { topicId });
        this.topic = topicManager.pluck(m => m.topic);

        const ideationManager = storage.get(IdeationManager, { topicId });
        this.isRankingStage = ideationManager.pluck(m => m.isRatingStage, false);
        this.isVotingStage = ideationManager.pluck(m => m.isVotingStage, false);

        ko.applyBindingsToNode(info.element, {
            'visible': this.canEdit
        });
    }

    edit = () => {
        this.comment()?.editing(true);
    }

    private checkIfCanBeEdited() {
        const discussion = this.discussion();
        const simpleTopic = this.simpleTopic();
        const topic = this.topic();
        const comment = this.comment();
        const createDate = comment?.createDate();

        if (discussion == undefined || topic == undefined || simpleTopic == undefined || comment == undefined || createDate == undefined)
            return false;

        if (comment.IsModeratorIdea())
            return false;

        if (comment.threadType() === enums.ThreadTypes.Video.value)
            return false;

        //These are simple media and can't be edited
        if (!comment.isModerator() && !comment.isObserver() && (simpleTopic.isScreenRecording() && !this.isModeratorUser() || simpleTopic.isAudioRecording()))
            return false;

        if (this.isRankingStage()) {
            //we don't allow to edit ideas in ranking stage
            if (comment.parentId() == undefined)
                return false;

            //you can change your vote only during voting
            if (comment.isIdeationVote())
                return this.isVotingStage();
        }        

        if (this.isModeratorUser()) {
            return !discussion.isHomework();
        }

        if (comment.canBeEdited()) {
            const projectEditTimeout = discussion.editCommentTimeout();
            const topicEditTimeout = topic.editCommentTimeout();

            const editTimeout = topicEditTimeout == -2 ? projectEditTimeout : topicEditTimeout;

            switch (editTimeout) {
                case -1: return true;
                case 0: return false;
                default: return ko.timeCondition({ before: DateHelpers.add(createDate, { minutes: editTimeout }) });
            }            
        }

        return false;
    }
}